<template>
  <div
    ref="containerEl"
    class="upcoming-task-list"
    :class="{ empty: upcomingBatches && upcomingBatches.length === 0 }"
  >
    <AddTaskButton
      class="add-task-button"
      :add-task-action="{ type: 'task-scheduling', date: tomorrow }"
      :metrics-source="'tasks/upcoming'"
    />
    <BaseEmptyTasklist
      v-if="isEmpty"
      text="Glance at all the tasks that you have planned"
    />
    <Suspense v-else>
      <div v-if="upcomingBatches" ref="taskListEl" class="weeks">
        <UpcomingWeek
          v-for="(week, index) in upcomingBatches"
          :key="`upcoming-${week.year}-${week.n}`"
          ref="upcomingWeekEls"
          :week="week"
          :week-index="index"
          :weeks-number="upcomingBatches.length"
          :task-list-el="taskListEl"
          :is-upcoming-selected="isUpcomingSelected"
          :container-el="containerEl"
          :show-all="showAll"
          :task-scope="taskScope"
          @unselect-all-tasks="unselectAllTasks"
          @update-lengths="(nbr: number) => updateLengths(index, nbr)"
        />
        <div class="unselect-area" @click="unselectAllTasks" />
      </div>
      <template #fallback>
        <Loader>
          <BaseSkeletonList />
        </Loader>
      </template>
    </Suspense>
  </div>
</template>
<script lang="ts">
import { addDays } from "date-fns";
import { computed, defineComponent, PropType, ref } from "vue";

import AddTaskButton from "@/components/AddTaskButton.vue";
import BaseEmptyTasklist from "@/components/base/BaseEmptyTasklist.vue";
import BaseSkeletonList from "@/components/base/BaseSkeletonList.vue";
import * as inject from "@/components/inject";
import Loader from "@/components/Loader.vue";
import type { TaskScope } from "@/components/tasks/taskScope";
import UpcomingWeek from "@/components/tasks/UpcomingWeek.vue";
import { useReleaser } from "@/ref";

export default defineComponent({
  name: "Upcoming",
  components: {
    AddTaskButton,
    Loader,
    UpcomingWeek,
    BaseEmptyTasklist,
    BaseSkeletonList,
  },
  props: {
    length: { type: Number, required: true },
    // Bypass the weeks' pagination and render every task, e.g. while the
    // host's find bar is open so the search covers the whole list.
    showAll: { type: Boolean, default: false },
    // When set, restrict the lists to the scope's tasks.
    taskScope: {
      type: Object as PropType<TaskScope>,
      required: false,
    },
  },
  emits: {
    "update:length": (_nbr: number) => true,
  },
  async setup(props, { emit }) {
    const controller = inject.controller();
    const clock = inject.clock();
    const upcomingWeekEls = ref<Array<InstanceType<typeof UpcomingWeek>>>([]);
    const taskListEl = ref<HTMLDivElement>();
    const containerEl = ref<HTMLDivElement>();
    const tomorrow = computed(() => addDays(clock.value, 1));
    const releaser = useReleaser();
    const upcomingBatches = await releaser.add(controller.task.upcoming());
    const isUpcomingSelected = computed(() =>
      upcomingWeekEls.value.map((el) => el.isWeekSelected).includes(true)
    );
    const lengths = ref<Map<number, number>>(new Map());
    const updateLengths = (index: number, nbr: number) => {
      lengths.value.set(index, nbr);
      emit(
        "update:length",
        Array.from(lengths.value.values()).reduce((a, b) => a + b, 0)
      );
    };
    // In a scope, weeks can turn out empty after filtering: fall back to
    // the empty state once every week has reported an empty (filtered) list.
    const isEmpty = computed<boolean>(() => {
      if (upcomingBatches.value && upcomingBatches.value.length === 0)
        return true;
      if (!props.taskScope) return false;
      return (
        upcomingBatches.value !== undefined &&
        lengths.value.size === upcomingBatches.value.length &&
        Array.from(lengths.value.values()).every((nbr) => nbr === 0)
      );
    });
    const unselectAllTasks = () =>
      upcomingWeekEls.value.forEach((el) => el.unselectTask());
    return {
      containerEl,
      isEmpty,
      isUpcomingSelected,
      lengths,
      taskListEl,
      tomorrow,
      unselectAllTasks,
      upcomingBatches,
      upcomingWeekEls,
      updateLengths,
    };
  },
});
</script>
<style scoped lang="scss">
.upcoming-task-list {
  margin-bottom: 16px;
  height: 100%;

  .add-task-button {
    margin-bottom: 16px;
    margin-left: -12px;
  }

  .weeks {
    display: flex;
    flex-direction: column;
    row-gap: 24px;
    flex: 1;
  }
}
</style>
